/**
 * RDAPanel Component
 * Displays Responsive Display Ads in an ad group with edit capability
 */

import React, { useCallback } from 'react';
import { LoadingSpinner } from '@/components/ui';
import type { AdGroup, Ad } from '@/types/campaign';
import type { RDACacheEntry } from '@/hooks/useRDACache';
import { getRDAsFromAdGroup } from '../utils';

interface RDAPanelProps {
  adGroup: AdGroup;
  rdaCache: Record<string, RDACacheEntry>;
  isLoadingRDAs: boolean;
  onEditRDA: (adId: string) => void;
}

export function RDAPanel({
  adGroup,
  rdaCache,
  isLoadingRDAs,
  onEditRDA,
}: RDAPanelProps) {
  const rdas = getRDAsFromAdGroup(adGroup);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (adId: string) => (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onEditRDA(adId);
      }
    },
    [onEditRDA]
  );

  // Get display text for an RDA, using cached data if available
  const getDisplayText = (rda: Ad): string => {
    const cached = rdaCache[rda.ad_id];
    type HeadlineType = string | { text: string; pinned_field?: string };
    const headlines: HeadlineType[] = cached?.headlines || rda.headlines || [];
    const headlineTexts = headlines.map(h =>
      typeof h === 'string' ? h : h.text
    );

    const longHeadline = cached?.long_headline || rda.long_headline;
    const allHeadlines = longHeadline
      ? [...headlineTexts, longHeadline]
      : headlineTexts;

    if (allHeadlines.length > 0) {
      return allHeadlines.slice(0, 2).join(' | ');
    }

    return rda.ad_name || `RDA ${rda.ad_id.slice(-6)}`;
  };

  if (rdas.length === 0) {
    return (
      <div className="p-4">
        <div className="text-center py-6 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-gray-600">No Responsive Display Ads in this ad group</p>
          <p className="text-sm text-gray-500 mt-1">
            Create RDAs in Google Ads to manage them here
          </p>
        </div>
      </div>
    );
  }

  if (isLoadingRDAs) {
    return (
      <div className="p-4">
        <div className="flex items-center justify-center py-6">
          <LoadingSpinner size="md" label="Loading ads" />
          <span className="ml-2 text-sm text-gray-500">Loading ads...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <p className="text-xs text-gray-500 mb-3">
        Click on a Responsive Display Ad to edit its text and image assets
      </p>

      <div className="space-y-2">
        {rdas.map((rda) => (
          <div
            key={rda.ad_id}
            role="button"
            tabIndex={0}
            className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            onClick={() => onEditRDA(rda.ad_id)}
            onKeyDown={handleKeyDown(rda.ad_id)}
            aria-label={`Edit RDA: ${getDisplayText(rda)}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 flex-1 min-w-0 mr-2">
                <span className={`text-xs font-medium flex-shrink-0 ${
                  rda.status === 'PAUSED' ? 'text-yellow-600' : 'text-blue-600'
                }`}>
                  RDA{rda.status === 'PAUSED' ? ' (Paused)' : ''}:
                </span>
                <p className="text-sm font-medium text-gray-900 truncate">
                  {getDisplayText(rda)}
                </p>
              </div>
              <span
                className="text-xs text-blue-600 hover:text-blue-800 flex-shrink-0"
                aria-hidden="true"
              >
                Edit
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default RDAPanel;
